import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";

const MonthlyProductionGraphModal = ({ data, onClose, orderTotals = {} }) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const groupedByOrderAndLine = {};

  (data || []).forEach((item) => {
    const date = new Date(item.date);
    if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) return;

    const order = item.cut_order_no || `Order-${item.order_id}`;
    const color = item.suborder_colour_name || `Color-${item.sub_order_id}`;
    const line = item.line?.trim();

    if (!line || line.toLowerCase() === "unknown") return;

    const key = `${order}__${line}`;

    if (!groupedByOrderAndLine[key]) groupedByOrderAndLine[key] = {};
    if (!groupedByOrderAndLine[key][color]) groupedByOrderAndLine[key][color] = 0;

    groupedByOrderAndLine[key][color] += item.total_piece;
  });

  const colorPalette = ["#4caf50", "#ff6f61"];

  const charts = Object.entries(orderTotals).map(([order, colorMap]) => {
    const linesData = (data || [])
      .filter(
        (d) =>
          d.cut_order_no === order &&
          new Date(d.date).getMonth() === currentMonth &&
          new Date(d.date).getFullYear() === currentYear &&
          d.line?.trim() &&
          d.line.toLowerCase() !== "unknown"
      )
      .reduce((acc, d) => {
        const line = d.line.trim();
        if (!acc[line]) acc[line] = {};
        acc[line][d.suborder_colour_name] = (acc[line][d.suborder_colour_name] || 0) + d.total_piece;
        return acc;
      }, {});

    if (Object.keys(linesData).length === 0) {
      linesData["No Line Info"] = {};
    }

    const orderTotalPlanned = Object.values(colorMap).reduce((a, b) => a + b, 0);
    const orderTotalCompleted = Object.entries(colorMap).reduce((sum, [color]) => {
      return (
        sum +
        Object.keys(linesData).reduce((s, line) => {
          const key = `${order}__${line}`;
          return s + (groupedByOrderAndLine[key]?.[color] || 0);
        }, 0)
      );
    }, 0);
    const orderTotalPending = Math.max(orderTotalPlanned - orderTotalCompleted, 0);

    return (
      <div
        key={order}
        className="bg-white border rounded-lg shadow p-4 w-full"
      >
        <h2 className="text-lg font-semibold text-indigo-800 mb-1">Order No: {order}</h2>
        <p className="text-sm text-gray-700 mb-3">
          📦 <strong>Order Quantity:</strong> {orderTotalPlanned} &nbsp; | &nbsp;
          ✅ <strong>Completed:</strong> {orderTotalCompleted} &nbsp; | &nbsp;
          ⏳ <strong>Pending:</strong> {orderTotalPending}
        </p>

        <div className="flex flex-col gap-6">
          {Object.entries(linesData).map(([line]) => {
            const key = `${order}__${line}`;
            let totalCompleted = 0;

            const chartData = Object.entries(colorMap).map(([color, planned]) => {
              const completed = groupedByOrderAndLine?.[key]?.[color] || 0;
              const pending = Math.max(planned - completed, 0);

              totalCompleted += completed;

              return {
                label: color,
                completed,
                pending,
                planned,
              };
            });

            const maxY = Math.max(...chartData.map((d) => d.planned), 100);

            return (
              <div
                key={line}
                className="bg-gray-50 border rounded-md p-4 w-full"
              >
                <h3 className="text-md font-semibold text-indigo-700 mb-1">
                  Line: <span className="text-sm text-gray-600">{line}</span>
                </h3>
                <p className="text-sm text-gray-600 mb-1">
                  📅 Month: <strong>{today.toLocaleString("default", { month: "long", year: "numeric" })}</strong>
                </p>

                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" angle={-20} textAnchor="end" interval={0} height={40} />
                    <YAxis domain={[0, maxY]} />
                    <Tooltip />
                    <Legend
                      layout="vertical"
                      verticalAlign="bottom"
                      align="left"
                      iconType="circle"
                      wrapperStyle={{
                        paddingTop: 10,
                        paddingLeft: 0,
                        lineHeight: '22px',
                      }}
                    />
                    <Bar dataKey="completed" stackId="a" fill={colorPalette[0]} name="✅ Completed" barSize={40}>
                      <LabelList dataKey="planned" position="top" fontSize={12} />
                    </Bar>
                    <Bar dataKey="pending" stackId="a" fill={colorPalette[1]} name="⏳ Pending" barSize={40}>
                      <LabelList
                        dataKey="pending"
                        position="top"
                        fontSize={12}
                        formatter={(val) => (val > 0 ? val : "")}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      </div>
    );
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white w-full h-full p-6 overflow-y-auto">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500"
          onClick={onClose}
        >
          ✖
        </button>
        <h2 className="text-2xl font-bold text-indigo-800 mb-6">📊 Monthly Production Summary</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
          {charts.length > 0 ? charts : <p className="text-gray-500">No data available for this month.</p>}
        </div>
      </div>
    </div>
  );
};

export default MonthlyProductionGraphModal;
